import {
    AlertTypes,
    AssertionDescriptorCategories,
    AssertionDescriptorValueDisplayTypes,
    AssertionDescriptorValueSourceTypeEnum,
    DataScopesEnum,
    EntityTypes,
    NotePurposeTypesEnum,
    NoteTypes,
    SortDirections,
    StrategySignificantChangesAssertionDescriptors,
    StrategyTypes,
    UserRightsEnum,
    ZoneContextType,
} from './Constants';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
    IEntityModel,
} from '../interfaces/IAssertionDescriptor';
import { INoteCategoryModel, INoteCategoryTypeModel, INoteModel } from '../interfaces/INote';

import Authorization from '../stores/Authorization';
import Common from '../stores/Common';
import { ICaseTitleModel } from '../interfaces/ICase';
import { IMultiSelectOption } from '../interfaces/ILookup';
import { IUserModel } from '../interfaces/IUser';
import Sort from '../stores/Sort';

export default class NoteHelper {
    static updateCurrentNoteOnCategoryChange(
        currentNote: INoteModel,
        optionsList: any,
        noteCategoryTypes: INoteCategoryTypeModel[]
    ) {
        const existingNoteCategories: INoteCategoryModel[] = currentNote.categories
            ? JSON.parse(JSON.stringify(currentNote.categories))
            : [];

        currentNote.categories = [];
        if (optionsList) {
            for (let i = 0; i < optionsList.length; i++) {
                const match = existingNoteCategories.filter((x) => x.id == optionsList[i].id);
                currentNote.categories.push({
                    id: optionsList[i].id,
                    name: optionsList[i].value,
                    displayName: optionsList[i].displayName,
                    assertions: match.length > 0 ? match[0].assertions : undefined,
                    subCategory: match.length > 0 ? match[0].subCategory : undefined,
                });
                const categoryLookupMatch = noteCategoryTypes.find(
                    (x) => x.id === optionsList[i].id
                );
                if (
                    categoryLookupMatch &&
                    (!categoryLookupMatch.subCategories ||
                        categoryLookupMatch.subCategories.length === 0)
                ) {
                    if (!currentNote.content && categoryLookupMatch.contentTemplateText)
                        currentNote.content = categoryLookupMatch.contentTemplateText;
                    if (!currentNote.context && categoryLookupMatch.contextTemplateText)
                        currentNote.context = categoryLookupMatch.contextTemplateText;
                }
            }
        }

        return currentNote;
    }

    static getDisplayValueForEntityAssertion = (
        assertions: IEntityAssertionModel[],
        assertionDescriptors: IAssertionDescriptorModel[]
    ) => {
        if (assertions.length == 0) return '';

        const assertion = assertions[0];

        const foundAD = assertionDescriptors.find(
            (ad) => ad.guid === assertion.assertionDescriptorGuid
        );

        switch (foundAD?.valueSourceType?.id) {
            case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                {
                    const foundADV = foundAD?.assertionDescriptorValues?.find(
                        (adv) => adv.guid === assertion.assertionDescriptorValueGuid
                    );

                    if (foundADV) {
                        return foundADV.text;
                    }
                }
                break;
            case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                switch (foundAD?.valueDisplayType?.id) {
                    case AssertionDescriptorValueDisplayTypes.DateTime.Value:
                        if (assertion.dateTimeValue) {
                            return Common.safeDateFormat(assertion.dateTimeValue);
                        }
                        break;
                    case AssertionDescriptorValueDisplayTypes.Number.Value:
                        {
                            if (assertion.numericValue)
                                return Math.round(assertion.numericValue).toString();
                        }
                        break;
                    case AssertionDescriptorValueDisplayTypes.Decimal.Value:
                        {
                            if (assertion.numericValue)
                                return assertion.numericValue.toFixed(2).toString();
                        }
                        break;
                    case AssertionDescriptorValueDisplayTypes.Percentage.Value:
                        {
                            if (assertion.numericValue)
                                return Math.round(assertion.numericValue).toString() + '%';
                        }
                        break;
                    case AssertionDescriptorValueDisplayTypes.Currency.Value:
                        {
                            if (assertion.numericValue)
                                return Common.formatCurrency(
                                    Math.round(assertion.numericValue).toString()
                                );
                        }
                        break;
                     case AssertionDescriptorValueDisplayTypes.CurrencyWithCents.Value:
                        {
                            if (assertion.numericValue)
                                return Common.formatCurrencyWithCents(
                                    Math.round(assertion.numericValue).toString()
                                );
                        }
                        break;
                    case AssertionDescriptorValueDisplayTypes.MultiSelect.Value:
                        {
                            const values: string[] = [];
                            assertions.forEach((x) => {
                                const advMatch = foundAD?.assertionDescriptorValues?.find(
                                    (adv) => adv.guid === x.assertionDescriptorValueGuid
                                );

                                if (advMatch && advMatch.text) {
                                    values.push(advMatch.text);
                                }
                            });

                            return values.join('|');
                        }
                    default:
                        return assertion.textValue;
                }
                break;
            case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                if (assertion.booleanValue !== undefined) {
                    return assertion.booleanValue ? 'Yes' : 'No';
                }
                break;
            default:
                break;
        }
    };

    static getNoteCategoryTypeOptionsForMultiselect = (
        noteCategoryTypes: INoteCategoryTypeModel[]
    ): IMultiSelectOption[] => {
        let noteCategoryTypeOptions: IMultiSelectOption[] = [];
        noteCategoryTypes.map((cat: INoteCategoryTypeModel) => {
            const item: IMultiSelectOption = {
                label: cat.displayName!,
                value: cat.name!,
                id: cat.id,
            }; // Use TypeId to hold the actual Status.Id unlike the other filters
            noteCategoryTypeOptions.push(item);
        });

        noteCategoryTypeOptions = noteCategoryTypeOptions.sort(Sort.compareValues('label'));
        return noteCategoryTypeOptions;
    };

    static getAssertionValue = (
        item: INoteModel,
        adName: string,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) => {
        if (
            item.categories &&
            item.categories.length > 0 &&
            item.categories[0].assertions &&
            item.categories[0].assertions.length > 0
        ) {
            let match: IEntityAssertionModel[] | undefined;
            if (adName.toUpperCase() === 'CHANGE')
                match = item.categories[0].assertions.filter(
                    (x) =>
                        x.assertionDescriptorGuid.toUpperCase() ==
                        StrategySignificantChangesAssertionDescriptors.Strategy_SignificantChanges_Change
                );
            else if (adName.toUpperCase() === 'DATE')
                match = item.categories[0].assertions.filter(
                    (x) =>
                        x.assertionDescriptorGuid.toUpperCase() ==
                        StrategySignificantChangesAssertionDescriptors.Strategy_SignificantChanges_Date
                );
            if (match)
                return NoteHelper.getDisplayValueForEntityAssertion(match, assertionDescriptors);
        }

        return '';
    };

    static getAssociatedAssertionDescriptorListForSignificantChanges = (
        assertions: IEntityAssertionModel[],
        assertionDescriptors: IAssertionDescriptorModel[]
    ) => {
        const list: IMultiSelectOption[] = [];

        for (let i = 0; i < assertions.length; i++) {
            const currentEntityAssertion = assertions[i];

            const foundAD = assertionDescriptors.find(
                (ad) => ad.guid === currentEntityAssertion.assertionDescriptorGuid
            );

            if (
                foundAD &&
                [
                    AssertionDescriptorCategories.Strategy_Principles.Value,
                    AssertionDescriptorCategories.Strategy_Goals.Value,
                    AssertionDescriptorCategories.Strategy_Situation.Value,
                ].includes(foundAD.category?.id ?? 0) &&
                !list.some((x) => x.guid === currentEntityAssertion.assertionDescriptorGuid)
            )
                list.push({
                    label: foundAD.category!.displayName! + ' - ' + foundAD.displayName!,
                    id: 0,
                    guid: currentEntityAssertion.assertionDescriptorGuid,
                    value: currentEntityAssertion.assertionDescriptorGuid!,
                });
        }

        return list;
    };

    static getSelectedValuesTextFromMultiSelect(selectedItems: IMultiSelectOption[]) {
        if (selectedItems.length == 0) return '';
        let title = 'SELECTED VALUES:';
        for (let i = 0; i < selectedItems.length; i++) {
            title = title + '\n' + selectedItems[i].label;
        }
        return title;
    }

    static hasSignificantChange = (adGuid: string, significantChanges: INoteModel[]) => {
        for (let i = 0; i < significantChanges.length; i++) {
            if (
                significantChanges[i].associations &&
                significantChanges[i].associations!.filter(
                    (x) =>
                        x.entityType &&
                        x.entityType.id === EntityTypes.AssertionDescriptor &&
                        x.entityGuid === adGuid
                ).length > 0
            ) {
                return true;
            }
        }
        return false;
    };

    static getAssociatedTopicsForSignificantChanges = (significantChange: INoteModel) => {
        if (significantChange.associations) {
            const output: string[] = [];
            significantChange.associations
                .filter((x) => x.displayName)
                .map((item: IEntityModel) => {
                    return output.push(item.displayName!);
                });
            return output.join('\n').toString();
        }

        return '';
    };

    static isUserAuthorizedToEditNote = (
        currentNote: INoteModel,
        user: IUserModel,
        userHasViewRight: boolean,
        userHasEditRight: boolean,
        userHasAddNewRight: boolean,
        currentZoneGuid?: string
    ) => {
        if (!currentNote.guid && userHasAddNewRight) return true; // A new note is being added
        if (userHasViewRight && !userHasEditRight) return false;
        if (userHasEditRight) {
            const dataScopeId = Authorization.getUserDefaultOrganizationDataScope(
                user,
                currentZoneGuid
            );
            switch (dataScopeId) {
                case DataScopesEnum.LocalBasic.Value:
                    if (currentNote.allowLocalEdit) return true;

                    // If the current action target is Local, then all users in that Local Counsel that have access to the case can edit the note.
                    if (
                        currentNote.type &&
                        currentNote.type.id === NoteTypes.CaseNote &&
                        currentNote.localCounselOrganizationGuid &&
                        currentNote.alert &&
                        currentNote.alert.users &&
                        currentNote.alert.users.filter(
                            (x) =>
                                x.type &&
                                x.type.id === AlertTypes.Act.Value &&
                                x.user &&
                                x.user.guid
                        ).length > 0
                    ) {
                        const actionTarget = currentNote.alert.users.filter(
                            (x) =>
                                x.type &&
                                x.type.id === AlertTypes.Act.Value &&
                                x.user &&
                                x.user.guid
                        )[0];
                        if (
                            actionTarget.dataScope &&
                            actionTarget.dataScope.id === DataScopesEnum.LocalBasic.Value
                        ) {
                            if (actionTarget.user!.guid!.toUpperCase() === user.guid!.toUpperCase())
                                return false; // If logged in user is the action target
                            if (
                                user.homeOrganization!.guid ===
                                currentNote.localCounselOrganizationGuid
                            )
                                return true; // If logged in User's home org is the same as case's Local Counsel Org
                        }
                    }

                    return false;
                default:
                    return true;
            }
        }
        return false;
    };

    static allowNoteEdit = (
        currentNote: INoteModel,
        loggedInUser: IUserModel,
        zoneContext: ZoneContextType,
        strategyTypeId?: number,
        parentEntityLocked?: boolean
    ) => {
        const noteTypeId = currentNote.type?.id;
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            loggedInUser,
            zoneContext.zoneGuid
        );
        let userHasEditRight = false;

        switch (noteTypeId) {
            case NoteTypes.CaseNote:
                userHasEditRight =
                    !parentEntityLocked &&
                    Authorization.userHasRight(
                        UserRightsEnum.EditCaseNote,
                        loggedInUser,
                        zoneContext.zoneGuid
                    );

                if (
                    userHasEditRight &&
                    loggedInUserDataScopeId === DataScopesEnum.LocalBasic.Value
                ) {
                    if (currentNote.allowLocalEdit) break;

                    userHasEditRight = false;

                    // If the current action target is Local, then all users in that Local Counsel that have access to the case can edit the note.
                    if (
                        currentNote.localCounselOrganizationGuid &&
                        currentNote.alert?.users?.some(
                            (x) =>
                                x.type &&
                                x.type.id === AlertTypes.Act.Value &&
                                x.user &&
                                x.user.guid
                        )
                    ) {
                        const actionTargetFilter = currentNote.alert.users.filter(
                            (x) =>
                                x.type &&
                                x.type.id === AlertTypes.Act.Value &&
                                x.user &&
                                x.user.guid
                        );
                        if (actionTargetFilter.length > 0) {
                            const actionTarget = actionTargetFilter[0];
                            if (
                                // Logged In LOCAL USER is the Action Target, Hence Can edit
                                (actionTarget?.user?.guid ?? '').toUpperCase() ===
                                (loggedInUser.guid ?? '').toUpperCase()
                            )
                                userHasEditRight = true;
                            else if (
                                // Action Target Home Org = Local Counsel
                                (actionTarget?.user?.homeOrganization?.guid ?? '').toUpperCase() ===
                                    (loggedInUser.homeOrganization?.guid ?? '').toUpperCase() &&
                                loggedInUser.homeOrganization?.guid ===
                                    currentNote.localCounselOrganizationGuid
                            )
                                userHasEditRight = true;
                        }
                    }
                }
                break;
            case NoteTypes.ZoneNote:
                userHasEditRight =
                    Authorization.userHasRight(
                        UserRightsEnum.EditZoneNote,
                        loggedInUser,
                        zoneContext.zoneGuid
                    ) &&
                    (loggedInUserDataScopeId !== DataScopesEnum.LocalBasic.Value ||
                        currentNote.allowLocalEdit === true);

                break;
            case NoteTypes.PackageSettlementNote:
                userHasEditRight =
                    !parentEntityLocked &&
                    Authorization.userHasRight(
                        UserRightsEnum.AddEditDeletePackageSettlement,
                        loggedInUser,
                        zoneContext.zoneGuid
                    );

                break;
            case NoteTypes.StrategyNote:
                switch (strategyTypeId) {
                    case StrategyTypes.StrategicPlan.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.EditStrategicPlan,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    case StrategyTypes.ExecutionPlan.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.EditExecutionPlan,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    case StrategyTypes.PlaintiffsFirm.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.AddEditDeletePlaintiffsFirmStrategy,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    case StrategyTypes.Jurisdiction.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.AddEditDeleteJurisdictionStrategy,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    case StrategyTypes.Projects.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.AddEditDeleteProject,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    case StrategyTypes.LocalCounsel.Value:
                        userHasEditRight = Authorization.userHasRight(
                            UserRightsEnum.AddEditDeleteLocalCounselStrategy,
                            loggedInUser,
                            zoneContext.zoneGuid
                        );

                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        return userHasEditRight;
    };

    static getNoteDecisionText = (item: INoteModel) => {
        let text = '';

        if (!item.purpose) return text;

        switch (item.purpose.id) {
            case NotePurposeTypesEnum.Acknowledgement:
                text = item.acknowledgmentStatus
                    ? item.acknowledgmentStatus.displayName || item.acknowledgmentStatus.name!
                    : '';
                    if (text.length > 0) {
                        if (item.acknowledgmentStatusLastModifiedBy) {
                            if (text) text += ' | ';
                            text +=
                                item.acknowledgmentStatusLastModifiedBy.profile!.firstName +
                                ' ' +
                                item.acknowledgmentStatusLastModifiedBy.profile!.lastName;
                        }

                        if (item.acknowledgmentStatusLastModifiedDate) {
                            if (text) text += ' | ';
                            text += Common.dateTimeFormatToLocal(
                                item.acknowledgmentStatusLastModifiedDate.toString()
                            );
                        }
                    }
                break;
            case NotePurposeTypesEnum.Approval:
                text = item.approvalStatus
                    ? item.approvalStatus.displayName || item.approvalStatus.name!
                    : '';
                    if (text.length > 0) {
                        if (item.approvalStatusLastModifiedBy) {
                            if (text) text += ' | ';
                            text +=
                                item.approvalStatusLastModifiedBy.profile!.firstName +
                                ' ' +
                                item.approvalStatusLastModifiedBy.profile!.lastName;
                        }

                        if (item.approvalStatusLastModifiedDate) {
                            if (text) text += ' | ';
                            text += Common.dateTimeFormatToLocal(
                                item.approvalStatusLastModifiedDate.toString()
                            );
                        }
                    }
                break;
            default:
        }

        return text;
    };

    static getModifiedByName = (user?: IUserModel) => {
        if (user && user.profile && user.profile.firstName && user.profile.lastName) {
            return user.profile.firstName.concat(' ', user.profile.lastName);
        }
        return '';
    };

    static getCaseTitle = (currentNote: INoteModel) => {
        if (currentNote.type && currentNote.type.id === NoteTypes.CaseNote) {
            const caseTitle: ICaseTitleModel = {
                caseName: currentNote.caseName,
                guid: currentNote.caseGuid,
                SQScore: currentNote.qScore,
                SQProjection: currentNote.qProjection,
            };
            return caseTitle;
        }
    };

    static sortNotesByStarredAndThenByModifiedDate = (noteA: INoteModel, noteB: INoteModel) => {
        return Sort.sortByTwoColumns(
            noteA,
            noteB,
            'starred',
            SortDirections.Desc,
            'modifiedDate',
            SortDirections.Desc
        );
    };
}
